<script lang="ts">
import { defineComponent } from 'vue'

/**
 * A banner to show CS agents they're impersonating a customer and with details.
 */
export default defineComponent({
  data() {
    return {
      isCollapsed: false,
      showDetails: false,
    }
  },
  computed: {
    customerAdminUrl(): string {
      return this.toApiAdminUrl(`admin/customer/${this.$userStore.user?.id}`)
    },
    customerName(): string {
      const firstName = this.$userStore.user?.first_name ?? ''
      const lastName = this.$userStore.user?.last_name ?? ''

      return `${firstName} ${lastName}`.trim()
    },
    memberDetails() {
      if (!this.$userStore.user) {
        return []
      }

      const { phone, email } = this.$userStore.user

      return [
        { label: 'Phone:', value: phone },
        { label: 'Email:', value: email },
      ]
    },
    activeSubscriptions() {
      return (
        this.$userStore.user?.subscriptions?.filter(
          (s) => s.status !== 'cancelled'
        ) ?? []
      )
    },
  },
  methods: {
    toApiAdminUrl(path: string): string {
      const API_URL = import.meta.env.GLP_API_URL.replace(/(api\/)$/gim, '')
      return `${API_URL}${path}`
    },
    toggleCollapsed() {
      if (!this.isCollapsed) {
        this.showDetails = false
      }

      this.isCollapsed = !this.isCollapsed
    },
    toggleDetails() {
      this.showDetails = !this.showDetails
    },
  },
})
</script>

<template>
  <div
    id="impersonate-banner"
    class="text-xs"
    :class="{ 'is-collapsed': isCollapsed }"
  >
    <div class="banner-title-row">
      <div class="banner-title-copy">
        <span class="banner-badge">Read-only</span>
        <p class="banner-title">Customer impersonation active</p>
        <p v-if="isCollapsed" class="banner-collapsed-customer">
          {{ customerName }}
        </p>
      </div>

      <div class="banner-actions">
        <button
          v-if="!isCollapsed"
          type="button"
          class="banner-toggle"
          @click="toggleDetails"
        >
          {{ showDetails ? 'Hide details' : 'Show details' }}
        </button>

        <button type="button" class="banner-toggle" @click="toggleCollapsed">
          {{ isCollapsed ? 'Show banner' : 'Hide banner' }}
        </button>
      </div>
    </div>

    <template v-if="!isCollapsed">
      <div class="banner-header mt-2 mb-2">
        <p class="banner-subtitle">Changes are blocked while impersonating.</p>

        <p class="banner-customer">
          Customer:
          <a
            :href="customerAdminUrl"
            target="_blank"
            class="font-black underline hover:no-underline"
          >
            {{ customerName }}
          </a>
        </p>

        <p class="banner-restrictions">
          Claims, deposits, withdrawals, upgrades, trial changes, and gameplay
          are disabled.
        </p>
      </div>
    </template>

    <template v-if="showDetails && !isCollapsed">
      <div
        v-for="(details, key) in memberDetails"
        :key
        class="mb-1 flex flex-wrap"
      >
        <p class="mr-2 flex-grow font-semibold">
          {{ details.label }}
        </p>
        <p>{{ details.value }}</p>
      </div>

      <div class="mb-1 flex flex-wrap">
        <p class="mr-2 flex-grow font-semibold">Subscriptions:</p>
        <div class="flex flex-col gap-0.5">
          <p v-for="sub in activeSubscriptions" :key="sub.id">
            <a
              :href="toApiAdminUrl(`admin/subscription/${sub.id}`)"
              target="_blank"
              class="underline hover:no-underline"
              >{{ sub.plan_name }}</a
            >
            (<strong
              >{{ sub.status
              }}{{ sub.status_raw === 'in_trial' ? ' : Trial' : '' }}</strong
            >)
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
#impersonate-banner {
  @apply text-white;

  z-index: 2147483638; // max z-index value
  position: fixed;
  top: 0;
  left: 50%;
  translate: -50% 0;
  padding: 10px 14px 12px;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  width: min(640px, calc(100vw - 28px));
  background-image:
    linear-gradient(rgb(0 0 0 / 0.12), rgb(0 0 0 / 0.12)),
    linear-gradient(
      135deg,
      rgb(24 122 58 / 0.78) 0%,
      rgb(58 160 95 / 0.72) 24%,
      rgb(124 58 237 / 0.74) 100%
    );
  background-color: rgb(54 104 85 / 0.68);
  background-size: cover;
  background-repeat: no-repeat;
  box-shadow: 0 10px 24px rgb(0 0 0 / 0.12);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  transition:
    width 0.2s ease,
    padding 0.2s ease,
    box-shadow 0.2s ease;
}

#impersonate-banner.is-collapsed {
  padding: 8px 12px 9px;
  width: min(360px, calc(100vw - 28px));
}

.banner-header {
  @apply flex flex-col gap-2;
}

.banner-title-row {
  @apply flex items-start justify-between gap-3;
}

.banner-actions {
  @apply flex shrink-0 items-center gap-3;
}

.banner-title-copy {
  @apply flex min-w-0 flex-col gap-1;
}

.banner-badge {
  @apply inline-flex w-fit rounded-full border border-white/40 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.18em];
  background: rgb(0 0 0 / 0.18);
}

.banner-title {
  @apply text-base font-black leading-tight;
}

.banner-collapsed-customer {
  @apply text-[13px] font-semibold leading-tight text-white/90;
}

.banner-subtitle {
  @apply text-[13px] font-semibold leading-tight text-white/95;
}

.banner-customer {
  @apply text-sm leading-tight;
}

.banner-restrictions {
  @apply text-[12px] leading-tight text-white/85;
}

.banner-toggle {
  @apply shrink-0 text-[11px] font-semibold underline underline-offset-2 hover:no-underline;
}

@media (max-width: 640px) {
  #impersonate-banner {
    padding: 10px 12px 12px;
  }

  .banner-title-row {
    @apply flex-col items-start gap-2;
  }

  .banner-actions {
    @apply flex-wrap gap-2;
  }

  #impersonate-banner.is-collapsed {
    width: min(320px, calc(100vw - 20px));
  }
}
</style>
